import { Button, Tag, Tooltip, Typography } from 'antd'

import { emptyCharacter } from '../../../components/EmptyChar'
import {
  buildSearchFilterProps,
  sortBooleanColumn,
  sortNumericColumn,
  sortStringColumn,
} from '../../../components/tables/utils'
import { getEnvironmentHost } from '../../../util/environment'
import { Tenant } from '../../Tenants/models'
import { userRoles, UserTenantTableItem } from '../models'

const options = userRoles.map((role: string) => ({ label: role, value: role }))

export const buildTenantsAccessTableColumns = ({
  setUserTenants,
  onChangeTenant,
  onDeleteUserTenantAccess,
  tenants,
  userTenants,
}: {
  setUserTenants: (userTenantTableItem: UserTenantTableItem[]) => void
  onChangeTenant: (documents: any) => void
  onDeleteUserTenantAccess: (userTenantTableItem: UserTenantTableItem) => void
  tenants: Tenant[]
  userTenants: UserTenantTableItem[]
}) => {
  const handleEditRole = (editedUserTenant: UserTenantTableItem) => {
    const editedUserTenants = [...userTenants]
    const userTenantToEditIndex = editedUserTenants.findIndex((userTenant) =>
      editedUserTenant.id
        ? editedUserTenant.id === userTenant.id
        : editedUserTenant.temporaryId === userTenant.temporaryId,
    )

    editedUserTenants[userTenantToEditIndex].role = editedUserTenant.role

    setUserTenants(editedUserTenants)
    onChangeTenant(editedUserTenant)
  }

  const handleEditDomain = (editedUserTenant: UserTenantTableItem) => {
    const editedUserTenants = [...userTenants]
    const userTenantToEditIndex = editedUserTenants.findIndex(
      (userTenant) => editedUserTenant.temporaryId === userTenant.temporaryId,
    )

    const selectedTenant = tenants.find(({ id }) => id === parseInt(editedUserTenant.domain))

    if (selectedTenant) {
      editedUserTenants[userTenantToEditIndex].id = selectedTenant.id
      editedUserTenants[userTenantToEditIndex].domain = selectedTenant.domain
      editedUserTenants[userTenantToEditIndex].name = selectedTenant.name
    }

    setUserTenants(editedUserTenants)
    onChangeTenant(editedUserTenant)
  }

  const tenantsOptions = tenants
    .filter(({ id: tenantId }) => !userTenants?.find(({ id }) => id === tenantId))
    .map(({ id, domain }) => ({ label: domain, value: id }))

  const selectProps = {
    autoFocus: true,
    defaultOpen: true,
  }

  return [
    {
      title: 'Name',
      dataIndex: 'name',
      width: '33%',
      sorter: sortNumericColumn({ dataIndex: 'name' }),
      ...buildSearchFilterProps({ dataIndex: 'name' }),
      className: 'tenant-name',
    },
    {
      title: 'Domain',
      dataIndex: 'domain',
      editable: true,
      options: tenantsOptions,
      width: '33%',
      autoFocus: true,
      defaultOpen: true,
      sorter: sortStringColumn({ dataIndex: 'domain' }),
      ...buildSearchFilterProps({ dataIndex: 'domain' }),
      onCell: (record: UserTenantTableItem) => ({
        record,
        selectProps,
        editable: record.temporaryId,
        options: tenantsOptions,
        dataIndex: 'domain',
        handleSave: handleEditDomain,
      }),
      render: (domain: string, record: UserTenantTableItem) =>
        record.temporaryId ? (
          domain
        ) : (
          <Typography.Link href={getEnvironmentHost({ domain: record.domain })} target="_blank">
            {record.domain}
          </Typography.Link>
        ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      editable: true,
      width: '33%',
      className: 'capitalize',
      sorter: sortStringColumn({ dataIndex: 'role' }),
      ...buildSearchFilterProps({ dataIndex: 'role' }),
      onCell: (record: UserTenantTableItem) => ({
        record,
        editable: true,
        options,
        dataIndex: 'role',
        selectProps,
        handleSave: handleEditRole,
      }),
      render: (role: string, record: UserTenantTableItem) =>
        record.role ? <Tooltip title="Click to update the role">{role}</Tooltip> : emptyCharacter,
    },
    {
      title: 'Nominate Only',
      dataIndex: 'nominateOnly',
      sorter: sortBooleanColumn({ dataIndex: 'nominateOnly' }),
      render: (nominateOnly: boolean) => (nominateOnly ? <Tag color="green">Yes</Tag> : 'No'),
    },
    {
      title: 'Actions',
      render: (_action: any, record: UserTenantTableItem) => (
        <Tooltip title="Remove user access to the tenant">
          <Button
            type="dashed"
            onClick={() => onDeleteUserTenantAccess({ ...record, status: 'deleted' })}
            disabled={!!record.temporaryId}
          >
            Remove
          </Button>
        </Tooltip>
      ),
    },
  ]
}
